import {
  type QueueFilters,
  type QueueQuery,
  type QueueSort,
  queueQuerySchema,
  type SortDir,
} from '@hrms/shared'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { TICKET_PARAM } from '../tickets/useTicketParam.ts'

/** URL keys the queue owns (everything else, like `?ticket=`, is left alone). */
export const QUEUE_PARAM_KEYS = [
  'q',
  'createdFrom',
  'createdTo',
  'status',
  'stage',
  'category',
  'priority',
  'raisedBy',
  'assignee',
  'overdue',
  'sort',
  'dir',
  'page',
] as const
type QueueParamKey = (typeof QUEUE_PARAM_KEYS)[number]

/** A filter change: a string, a list (multi-value filters, written `a,b`), or empty to remove. */
export type FilterPatch = Partial<
  Record<Exclude<QueueParamKey, 'sort' | 'dir' | 'page'>, string | readonly string[] | undefined>
>

/** URL keys the board ignores (columns split by status; each column has its own order). */
export const NOT_ON_BOARD = ['status', 'stage', 'sort', 'dir', 'page'] as const

/** `search` minus the keys the board ignores (the board API query, and the list → board link). */
export function boardSearchOf(search: string): string {
  const p = new URLSearchParams(search)
  for (const k of NOT_ON_BOARD) p.delete(k)
  return p.toString()
}

const toParam = (v: string | readonly string[] | undefined) =>
  typeof v === 'string' ? v : v?.join(',')

const EMPTY: QueueQuery = queueQuerySchema.parse({})

/** The queue's slice of a query string, in a stable key order (the API query string). */
function queueSearchOf(params: URLSearchParams): string {
  const out = new URLSearchParams()
  for (const k of QUEUE_PARAM_KEYS) {
    const v = params
      .getAll(k)
      .filter((x) => x !== '')
      .join(',')
    if (v) out.set(k, v)
  }
  return out.toString()
}

function parseQueueSearch(search: string): QueueQuery {
  const parsed = queueQuerySchema.safeParse(Object.fromEntries(new URLSearchParams(search)))
  return parsed.success ? parsed.data : EMPTY
}

/**
 * The address bar right now. Navigations render in a transition, so the `searchParams` a render
 * saw can lag behind quick successive edits (ticking two values fast); reading the live URL keeps
 * each edit building on the last.
 */
const liveParams = () => new URLSearchParams(window.location.search)

/** A patch, or a function of the current filters returning one (for toggles). */
export type FilterUpdate = FilterPatch | ((current: QueueFilters) => FilterPatch)

/**
 * Filters, sort and page live in the URL query string (shareable; back/forward works). Filter
 * changes push a history entry and reset the page; typing in search replaces instead.
 * Multi-value filters are one comma-separated key (`?priority=high,urgent`); repeated keys in a
 * pasted link are read too.
 */
export function useQueueParams() {
  const [params, setParams] = useSearchParams()

  const search = useMemo(() => queueSearchOf(params), [params])
  const query = useMemo(() => parseQueueSearch(search), [search])

  const update = useCallback(
    (
      patch:
        | Partial<Record<QueueParamKey, string | readonly string[] | undefined>>
        | ((
            current: QueueQuery,
          ) => Partial<Record<QueueParamKey, string | readonly string[] | undefined>>),
      opts: { replace?: boolean } = {},
    ) =>
      setParams(
        () => {
          const next = liveParams()
          const resolved =
            typeof patch === 'function' ? patch(parseQueueSearch(queueSearchOf(next))) : patch
          for (const [k, raw] of Object.entries(resolved)) {
            const v = toParam(raw)
            if (v) next.set(k, v)
            else next.delete(k)
          }
          return next
        },
        { replace: opts.replace },
      ),
    [setParams],
  )

  const setFilters = useCallback(
    (patch: FilterUpdate, opts: { replace?: boolean } = {}) =>
      update(
        (current) => ({
          ...(typeof patch === 'function' ? patch(current) : patch),
          page: undefined,
        }),
        opts,
      ),
    [update],
  )

  const clearFilters = useCallback(
    () =>
      setParams(() => {
        const prev = liveParams()
        const next = new URLSearchParams()
        for (const k of ['sort', 'dir', TICKET_PARAM]) {
          const v = prev.get(k)
          if (v) next.set(k, v)
        }
        return next
      }),
    [setParams],
  )

  const setSort = useCallback(
    (sort: QueueSort, dir: SortDir) => update({ sort, dir, page: undefined }),
    [update],
  )
  const setPage = useCallback(
    (page: number) => update({ page: page > 1 ? String(page) : undefined }),
    [update],
  )

  const filters: QueueFilters = query
  return { query, filters, search, setFilters, clearFilters, setSort, setPage }
}
